import type { ChangeEventHandler } from "react";
import { useState } from "react";
import { styled } from "styled-components";

import { Checkbox, StyledModalText, UIButton } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { deactivateModal } from "domains/modal/actions";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import { getModalParams } from "domains/modal/selectors";
import type { ModalParams } from "domains/modal/types";
import { rejectTopicKnowledge } from "domains/pinAddReject/actions";

const StyledCheckboxFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  margin-bottom: 50px;
`;

const REJECTION_REASONS = {
  offensiveContent: { id: "Offensive content", term: "app.reject_content.reason.offensive_content" },
  uninterestingContent: { id: "Uninteresting content", term: "app.reject_content.reason.uninteresting_content" },
  poorMetadataQuality: { id: "Poor meta-data quality", term: "app.reject_content.reason.poor_metadata_quality" },
  factuallyIncorrect: { id: "Factually inaccurate", term: "app.reject_content.reason.factually_incorrect" },
} as const;

export const KnowledgeRejectionReasonModal = () => {
  const [checkedElementId, setCheckedElementId] = useState<string>(REJECTION_REASONS.offensiveContent.id);

  const dispatch = useDispatch();
  const { knowledgeId, knowledgeType, onSuccessPath, topicId } = useSelector(
    getModalParams,
  ) as ModalParams<"KnowledgeRejectionReason">;
  const translate = useTranslate();

  const onCheckboxClick: ChangeEventHandler<HTMLInputElement> = (event) => {
    setCheckedElementId(event.target.name);
  };

  const onClick = () => {
    dispatch(
      rejectTopicKnowledge({
        knowledgeId,
        knowledgeType,
        onSuccessPath,
        rejectionReason: checkedElementId,
        topicId,
      }),
    );
    dispatch(deactivateModal());
  };

  return (
    <>
      <ModalHeader title={translate("app.reject_content.title")} />
      <StyledModalText>{translate("app.reject_content.reason.title")}</StyledModalText>
      <StyledCheckboxFormContainer>
        {Object.values(REJECTION_REASONS).map((rejectionReason) => (
          <Checkbox
            id={rejectionReason.id}
            key={rejectionReason.id}
            onChange={onCheckboxClick}
            text={translate(rejectionReason.term)}
            value={checkedElementId === rejectionReason.id}
          />
        ))}
      </StyledCheckboxFormContainer>
      <UIButton fullwidth onClick={onClick}>
        {translate("app.reject_content.reason.save")}
      </UIButton>
    </>
  );
};
